import { useMemo } from "react"
import { addMinutes } from "date-fns"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import { DotText } from "@/v2-backoffice-client/components/dot"
import Table, {
  createColumns,
  useTableQuery,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import { useInvitationQuery } from "@/v2-backoffice-client/invitation/invitation.query"
import type { InvitationFragment } from "@/v2-backoffice-client/invitation/__types__/invitation.fragment"
import InvitationLogPopover from "@/v2-backoffice-client/invitation/log/InvitationLogPopover"
import InvitationResendManyButton from "@/v2-backoffice-client/invitation/resend/many/InvitationResendManyButton"
import InvitationRemoveManyButton from "@/v2-backoffice-client/invitation/remove/many/InvitationRemoveManyButton"
import InvitationTableRowToolbar from "@/v2-backoffice-client/invitation/InvitationTableRowToolbar"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"

function formatRelativeExpiry(expiresAt: Date) {
  const now = new Date()
  const diffMs = expiresAt.getTime() - now.getTime()

  if (diffMs <= 0) {
    const agoMs = -diffMs
    const agoMinutes = Math.round(agoMs / 60_000)
    if (agoMinutes < 60) return `Expired ${agoMinutes}m ago`
    const agoHours = Math.round(agoMs / 3_600_000)
    if (agoHours < 24) return `Expired ${agoHours}h ago`
    const agoDays = Math.round(agoMs / 86_400_000)
    return `Expired ${agoDays}d ago`
  }

  const totalMinutes = Math.round(diffMs / 60_000)
  if (totalMinutes <= 60) return `${totalMinutes}m left`

  const totalHours = diffMs / 3_600_000
  if (totalHours <= 24) return `${Math.round(totalHours)}h left`

  const totalDays = diffMs / 86_400_000
  const roundedDays = Math.round(totalDays)
  if (roundedDays <= 1) return `${Math.round(totalHours)}h left`
  return `${roundedDays}d left`
}

const PAGE_SIZE = 100

const COLUMN_SIZING = {
  Email: 200,
  Name: 150,
  TenantName: 150,
  UserTenantType: 80,
  LatestLog: 100,
  ConsumedByUser: 130,
  ExpiresIn: 110,
}

function createInvitationColumns() {
  return createColumns<InvitationFragment>({
    enableSelection: true,
    enableSorting: true,
    enableHiding: true,
    columns: {
      Email: {
        id: "Email",
        label: "Email",
        filter: { type: "text", placeholder: "Email..." },
        to: ({ row }) => `?v=invitation:${row.original.Id}`,
      },
      Name: {
        id: "Name",
        label: "Name",
        // Server-side: the composite Name entry in invitationsFilterRegistry.
        filter: {
          type: "text",
          placeholder: "Name...",
          operators: ["CONTAINS", "EQ"],
        },
        getValue: (row) => formatUserName(row.FirstName, row.LastName),
        renderCell({ row }) {
          return formatUserName(row.original.FirstName, row.original.LastName)
        },
      },
      TenantName: {
        id: "TenantName",
        label: "Team",
        // Server-side: EXISTS against acc.Tenants by the invitation's
        // TenantId (invitationsFilterRegistry).
        filter: {
          type: "text",
          placeholder: "Team name...",
          operators: ["CONTAINS", "EQ"],
        },
        getValue: (row) => row.Tenant?.Name,
        to: ({ row }) => `?v=tenant:${row.original.TenantId}`,
        renderCell({ row }) {
          return row.original.Tenant?.Name ?? null
        },
      },
      UserTenantType: {
        id: "UserTenantType",
        label: "Role",
        enableGrouping: true,
        getValue: (row) => row.UserTenantType?.Name,
        filter: {
          type: "multi-select",
          placeholder: "Any role",
          options: [
            { label: "Admin", value: "1" },
            { label: "User", value: "2" },
            { label: "Viewer", value: "3" },
          ],
          operators: ["EQ", "NEQ", "IN"],
        },
      },
      Referral: {
        id: "Referral",
        label: "Referral",
        defaultHidden: true,
      },
      LatestLog: {
        id: "LatestLog",
        label: "Status",
        getValue: (row) => row.LatestLog?.StatusName,
        renderCell({ row }) {
          return (
            <InvitationLogPopover
              latestLog={row.original.LatestLog}
              logs={row.original.Logs}
            />
          )
        },
      },
      ConsumedByUser: {
        id: "ConsumedByUser",
        label: "User",
        getValue: (row) =>
          row.ConsumedByUser
            ? formatUserName(
                row.ConsumedByUser.FirstName,
                row.ConsumedByUser.LastName,
              )
            : undefined,
        to: ({ row }) =>
          row.original.ConsumedByUser
            ? `?v=user:${row.original.ConsumedByUser.Id}`
            : undefined,
      },
      ExpiresIn: {
        id: "ExpiresIn",
        label: "Expires In",
        getValue: (row) => {
          return addMinutes(new Date(row.CreatedAt), row.MinuteLimit).getTime()
        },
        renderCell({ row }) {
          const inv = row.original
          const createdAt = new Date(inv.CreatedAt)
          const expiresAt = addMinutes(createdAt, inv.MinuteLimit)
          const isExpired = expiresAt.getTime() <= Date.now()
          return (
            <DotText variant={isExpired ? "destructive" : "success-1"}>
              {formatRelativeExpiry(expiresAt)}
            </DotText>
          )
        },
        exportValue({ row }) {
          const inv = row.original
          const expiresAt = addMinutes(new Date(inv.CreatedAt), inv.MinuteLimit)
          return formatRelativeExpiry(expiresAt)
        },
      },
      Id: { id: "Id", label: "Id", defaultSize: 50, defaultHidden: true },
    },
  })
}

export type InvitationTableProps = Omit<
  TableProps<InvitationFragment>,
  "data" | "columns"
>

function InvitationTable(props: InvitationTableProps) {
  const { className, ...tableProps } = props

  const opener = useAppViewSheetOpener()
  const columns = useMemo(() => createInvitationColumns(), [])

  const {
    items,
    pageInfo,
    loading,
    error,
    refetch,
    onFetchMore,
    onQueryChange,
  } = useTableQuery({
    pageSize: PAGE_SIZE,
    useQuery: useInvitationQuery,
    select: (data) => data.getInvitations,
  })

  return (
    <Table
      className={cn("invitation-table", className)}
      tableId="invitation-table"
      data={items}
      columns={columns}
      initialColumnSizing={COLUMN_SIZING}
      enableGrouping
      enableRowSelection
      manualSorting
      manualFiltering
      searchColumn="Search"
      onQueryChange={onQueryChange}
      getHighlightedRowId={(row) => {
        return opener.getIsActive("invitation", row.original.Id)
      }}
      {...tableProps}
    >
      <Table.Toolbar>
        <div className="flex-1" />
        <Table.Search placeholder="Search by name or email..." />

        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu onRefresh={refetch} exportFileName="invitations" />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.RowToolbar<InvitationFragment> column="Email">
        {(invitation) => <InvitationTableRowToolbar invitation={invitation} />}
      </Table.RowToolbar>

      <Table.Content
        countLabel="invitations"
        isLoading={loading}
        error={error}
        pageInfo={pageInfo}
        onFetchMore={onFetchMore}
      />

      <Table.SelectionToolbar<InvitationFragment>>
        {(invitations, reset) => {
          const ids = invitations.map((inv) => inv.Id)
          return (
            <>
              <Table.ExportSelectionButton
                fileName="invitations"
                selectionCount={invitations.length}
              />
              <InvitationResendManyButton
                isStacked
                invitationIds={ids}
                onCompleted={reset}
              >
                Resend
              </InvitationResendManyButton>
              <InvitationRemoveManyButton
                isStacked
                invitationIds={ids}
                onCompleted={reset}
              >
                Remove
              </InvitationRemoveManyButton>
            </>
          )
        }}
      </Table.SelectionToolbar>
    </Table>
  )
}

export default InvitationTable
